import { LitElement, html } from 'lit';
import { t } from '../i18n';
import type { ProfileState } from './shared';
import { defineZenComponent } from './register';
import type { ProfileDraft } from '../profile';
import { EMPTY_AI_WORK_DRAFT, serializeAiWorkDraft } from '../ai-work-draft-logic';
import './ai-work-cards';

export type ProfileSectionId = 'profile' | 'ai-pref' | 'ai-work' | 'advanced';

export type ProfileSectionsFormData = ProfileDraft;

const defaultFormData = (): ProfileSectionsFormData => ({
  profDisplayName: '',
  profEmail: '',
  profDisplayNames: '',
  profLocation: '',
  profLocation2: '',
  profWeekdayDaytimeLocation: 'first',
  profTimezone: '',
  profLanguage: 'ja',
  prefTravelMode: 'train',
  prefMeetingDefaultAttendance: 'auto',
  prefEventDefaultAttendance: 'auto',
  prefUnreadDeadline: 'same_day',
  prefAccPaymentsExec: '',
  prefAccPaymentsSystem: '',
  prefAccInvoicesExec: '',
  prefAccInvoicesSystem: '',
  prefAccExpensesExec: '',
  prefAccExpensesSystem: '',
  prefAiWork: serializeAiWorkDraft(EMPTY_AI_WORK_DRAFT),
  prefCommExternal: 'formal',
  prefCommInternal: 'auto',
  prefTaskGranularity: 'normal',
});

const SELECT_STYLE = 'width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);font-family:inherit;font-size:13px;background:var(--surface);margin-top:4px;';

export class ZenProfileSections extends LitElement {
  static properties = {
    state: { attribute: false },
    formData: { attribute: false },
    sections: { type: String },
    source: { type: String },
  };

  declare state: ProfileState;
  declare formData: ProfileSectionsFormData;
  declare sections: string;
  declare source: 'settings';

  constructor() {
    super();
    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',
      timezoneOptions: [],
    };
    this.formData = defaultFormData();
    this.sections = 'profile,ai-pref,ai-work,advanced';
    this.source = 'settings';
  }

  createRenderRoot() {
    return this;
  }

  private _onLangChange = () => this.requestUpdate();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this._onLangChange);
  }

  disconnectedCallback() {
    document.removeEventListener('zen-lang-change', this._onLangChange);
  }

  private get activeSections(): ProfileSectionId[] {
    return this.sections.split(',').map((s) => s.trim()).filter(Boolean) as ProfileSectionId[];
  }

  private has(id: ProfileSectionId): boolean {
    return this.activeSections.includes(id);
  }

  private updateField<K extends keyof ProfileSectionsFormData>(key: K, value: ProfileSectionsFormData[K]) {
    if (this.formData[key] === value) return;
    this.formData = { ...this.formData, [key]: value };
    this.dispatchEvent(new CustomEvent('profile-field-change', {
      bubbles: true,
      composed: true,
      detail: { key, value, source: this.source },
    }));
  }

  private handleFieldEvent(event: Event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.id || target.id === 'profEmail' || !(target.id in this.formData)) return;
    this.updateField(target.id as keyof ProfileSectionsFormData, target.value);
  }

  private renderProfileSection() {
    return html`
      <div class="field">
        <label>${t('profileDisplayName')}</label>
        <input type="text" id="profDisplayName" .value=${this.formData.profDisplayName} placeholder=${t('profileDisplayNamePlaceholder')}>
        <small class="field-hint">${t('profileDisplayNameHint')}</small>
      </div>
      <div class="field">
        <label>${t('profileEmail')}</label>
        <div id="profEmail" class="profile-readback" role="text">
          ${this.formData.profEmail || t('profileEmailUnavailable')}
        </div>
      </div>
      <div class="field">
        <label>${t('profileName')}</label>
        <input type="text" id="profDisplayNames" .value=${this.formData.profDisplayNames} placeholder=${t('profileNamePlaceholder')}>
        <small class="field-hint">${t('profileNameHint')}</small>
      </div>
      <div class="field">
        <label>${t('profileLocation')}</label>
        <input type="text" id="profLocation" .value=${this.formData.profLocation} placeholder=${t('profileLocationPlaceholder')}>
        <small class="field-hint">${t('profileLocationHint')}</small>
      </div>
      <div class="field">
        <label>${t('profileLocation2')}</label>
        <input type="text" id="profLocation2" .value=${this.formData.profLocation2} placeholder=${t('profileLocation2Placeholder')}>
      </div>
      <div class="field">
        <label>${t('profileWeekdayDaytimeLocation')}</label>
        <select id="profWeekdayDaytimeLocation" .value=${this.formData.profWeekdayDaytimeLocation} style=${SELECT_STYLE}>
          <option value="first" ?selected=${this.formData.profWeekdayDaytimeLocation !== 'second'}>${t('profileLocation')}</option>
          <option value="second" ?selected=${this.formData.profWeekdayDaytimeLocation === 'second'}>${t('profileLocation2')}</option>
        </select>
        <small class="field-hint">${t('profileWeekdayDaytimeLocationHint')}</small>
      </div>
      <div class="field">
        <label>${t('profileTimezone')}</label>
        <select id="profTimezone" style="width:100%;padding:8px;border:1px solid var(--border);border-radius:var(--radius);font-family:inherit;font-size:13px;background:var(--surface);">
          ${this.state.timezoneOptions.map((option) => html`<option value=${option.value} ?selected=${option.value === this.formData.profTimezone}>${option.label}</option>`)}
        </select>
        <small class="field-hint">${t('profileTimezoneHint')}</small>
      </div>
      <div class="field">
        <label>${t('profileLanguage')}</label>
        <select id="profLanguage" .value=${this.formData.profLanguage} style="width:100%;padding:8px;border:1px solid var(--border);border-radius:var(--radius);font-family:inherit;font-size:13px;background:var(--surface);">
          <option value="ja">日本語</option>
          <option value="en">English</option>
        </select>
        <small class="field-hint">${t('profileLanguageHint')}</small>
      </div>
    `;
  }

  // #2762: travel and attendance moved to 日程調整 in 「AIに任せる業務」.
  private renderAiPrefSection() {
    return html`
      <div class="pref-field" style="padding-left:0;">
        <label>${t('prefUnreadDeadline')}</label>
        <select id="prefUnreadDeadline" .value=${this.formData.prefUnreadDeadline} style=${SELECT_STYLE}>
          <option value="same_day">${t('prefUnreadDeadlineSameDay')}</option>
          <option value="next_business_day">${t('prefUnreadDeadlineNextBusinessDay')}</option>
        </select>
        <small class="field-hint">${t('prefUnreadDeadlineHint')}</small>
      </div>
    `;
  }

  // #2762: business tasks, their services and the tools each AI declared.
  private renderAiWorkSection() {
    return html`<zen-ai-work-cards .formData=${this.formData}
      .onField=${(key: keyof ProfileSectionsFormData, value: string) => this.updateField(key, value)}></zen-ai-work-cards>`;
  }

  // #2762: the two tone fallbacks moved to メールの返信 and 社内の連絡.
  private renderAdvancedSection() {
    return html`
      <div class="pref-field" style="margin:0;">
        <label>${t('prefTaskGranularity')}</label>
        <select id="prefTaskGranularity" .value=${this.formData.prefTaskGranularity} style=${SELECT_STYLE}>
          <option value="fine">${t('prefTaskFine')}</option>
          <option value="normal">${t('prefTaskNormal')}</option>
          <option value="coarse">${t('prefTaskCoarse')}</option>
        </select>
        <small class="field-hint">${t('prefTaskGranularityHint')}</small>
      </div>
    `;
  }

  render() {
    const isModalLayout = this.activeSections.length > 1;
    return html`
      <div class="profile-sections-fields" @input=${this.handleFieldEvent} @change=${this.handleFieldEvent}>
      ${this.has('profile') ? this.renderProfileSection() : ''}
      ${this.has('ai-pref') ? html`
        <div class="profile-pref-section">
          ${isModalLayout ? html`<label class="field-label">${t('profileAIPref')}</label>` : ''}
          ${this.renderAiPrefSection()}
          ${this.has('ai-work') ? html`
            <label class="field-label" style="margin-top:12px;">${t('settingsNavAiWork')}</label>
            ${this.renderAiWorkSection()}
          ` : ''}
          ${this.has('advanced') ? (isModalLayout ? html`
            <details class="profile-advanced">
              <summary>
                <span>${t('profileAdvancedSettings')}</span>
                <span class="mi mi-sm">expand_more</span>
              </summary>
              <div class="profile-advanced-body">
                ${this.renderAdvancedSection()}
              </div>
            </details>
          ` : this.renderAdvancedSection()) : ''}
        </div>
      ` : html`
        ${this.has('ai-work') ? this.renderAiWorkSection() : ''}
        ${this.has('advanced') ? this.renderAdvancedSection() : ''}
      `}
      </div>
    `;
  }
}

defineZenComponent('zen-profile-sections', ZenProfileSections);
